import { LEGACY_MODEL_EXCLUDED_SLUGS } from './legacyModelSlugs';
import type { Preset } from './presets';

export interface AiModelOption {
  title: string;
  description: string;
  value: string;
  current?: boolean;
  isNew?: boolean;
  requiresUpgrade?: boolean;
}

// The full lineup of AI models we offer. Shared between the logged-in template
// form (GenericComponent) and the public/landing template form
// (PublicTemplateForm) so both always showcase the same models.
export const aiModelOptions: AiModelOption[] = [
  {
    title: 'Standard model',
    description: 'Fast model for everyday tasks',
    value: 'small',
  },
  {
    title: 'Advanced model',
    description: 'Powerful model for complex tasks',
    value: 'large',
    requiresUpgrade: true,
  },
  {
    title: 'Reasoning model',
    description: 'Uses advanced reasoning',
    value: 'reasoning',
    isNew: true,
    requiresUpgrade: true,
  },
  {
    title: 'GPT-6 Astra',
    description: "OpenAI's most capable model for the hardest work",
    value: 'gpt-6-astra',
    isNew: true,
    requiresUpgrade: true,
  },
  {
    title: 'GPT-6.1 Sol',
    description: 'Upgraded Sol with strong coding at a lower cost',
    value: 'gpt-6.1-sol',
    isNew: true,
    requiresUpgrade: true,
  },
  {
    title: 'GPT-6 Sol',
    description: 'Fast, affordable GPT-6 for work at scale',
    value: 'gpt-6-sol',
    isNew: true,
    requiresUpgrade: true,
  },
  {
    title: 'GPT-6 Luna',
    description: 'Fastest, lowest-cost GPT-6 for high-volume tasks',
    value: 'gpt-6-luna',
    isNew: true,
    requiresUpgrade: true,
  },
  {
    title: 'GPT-5.6 Sol',
    description: 'Frontier model for complex professional work',
    value: 'gpt-5.6-sol',
    isNew: true,
    requiresUpgrade: true,
  },
  {
    title: 'GPT-5.6 Terra',
    description: 'Strong intelligence with balanced cost',
    value: 'gpt-5.6-terra',
    isNew: true,
    requiresUpgrade: true,
  },
  {
    title: 'GPT-5.6 Luna',
    description: 'Fast, affordable model for high-volume tasks',
    value: 'gpt-5.6-luna',
    isNew: true,
    requiresUpgrade: true,
  },
  {
    title: 'GPT-5.5',
    description: 'Powerful model for complex tasks',
    value: 'gpt-5.5-instant',
    isNew: true,
    requiresUpgrade: true,
  },
  {
    title: 'GPT-5 mini',
    description: 'Fast model for everyday tasks',
    value: 'gpt-5-mini',
    isNew: true,
    requiresUpgrade: true,
  },
  {
    title: 'Claude Fable 5.1',
    description: 'Most capable model from Anthropic',
    value: 'claude-fable-5-1',
    isNew: true,
    requiresUpgrade: true,
  },
  {
    // Billing alias: runs Fable 5.1 with adaptive thinking on; thinking tokens
    // are billed on top of the output (see @/data/completions.ts).
    title: 'Claude Fable 5.1 Reasoning',
    description: 'Fable 5.1 with deep thinking. Reasoning tokens are billed',
    value: 'claude-fable-5-1-reasoning',
    isNew: true,
    requiresUpgrade: true,
  },
  {
    title: 'Claude Fable 5',
    description: 'Previous most capable model from Anthropic',
    value: 'claude-fable-5',
    isNew: false,
    requiresUpgrade: true,
  },
  {
    // Billing alias: runs Fable 5 with adaptive thinking on; thinking tokens
    // are billed on top of the output (see @/data/completions.ts).
    title: 'Claude Fable 5 Reasoning',
    description: 'Fable 5 with deep thinking. Reasoning tokens are billed',
    value: 'claude-fable-5-reasoning',
    isNew: false,
    requiresUpgrade: true,
  },
  {
    title: 'Claude Opus 5.5',
    description: 'Newest flagship Opus from Anthropic',
    value: 'claude-opus-5-5',
    isNew: true,
    requiresUpgrade: true,
  },
  {
    // Billing alias: runs Opus 5.5 with adaptive thinking at effort 'high';
    // thinking tokens are billed on top of the output (see @/data/completions.ts).
    title: 'Claude Opus 5.5 Reasoning',
    description: 'Opus 5.5 with deep thinking. Reasoning tokens are billed',
    value: 'claude-opus-5-5-reasoning',
    isNew: true,
    requiresUpgrade: true,
  },
  {
    title: 'Claude Opus 5',
    description: 'Previous flagship Opus from Anthropic',
    value: 'claude-opus-5',
    isNew: false,
    requiresUpgrade: true,
  },
  {
    // Billing alias: runs Opus 5 with adaptive thinking on; thinking tokens
    // are billed on top of the output (see @/data/completions.ts).
    title: 'Claude Opus 5 Reasoning',
    description: 'Opus 5 with deep thinking. Reasoning tokens are billed',
    value: 'claude-opus-5-reasoning',
    isNew: true,
    requiresUpgrade: true,
  },
  {
    title: 'Claude Opus 4.8',
    description: 'Previous flagship model from Anthropic',
    value: 'claude-opus-4-8',
    isNew: false,
    requiresUpgrade: true,
  },
  {
    title: 'Claude Sonnet 5.5',
    description: 'Newest fast, agentic Sonnet from Anthropic',
    value: 'claude-sonnet-5-5',
    isNew: true,
    requiresUpgrade: true,
  },
  {
    title: 'Claude Sonnet 5',
    description: 'Previous Sonnet from Anthropic',
    value: 'claude-sonnet-5',
    isNew: false,
    requiresUpgrade: true,
  },
  {
    title: 'Claude Sonnet 4.6',
    description: 'Fast, capable model from Anthropic',
    value: 'claude-sonnet-4-6',
    isNew: false,
    requiresUpgrade: true,
  },
  {
    title: 'Claude Haiku 5.5',
    description: 'Fastest, lowest-cost model from Anthropic',
    value: 'claude-haiku-5-5',
    isNew: true,
    requiresUpgrade: true,
  },
];

export const geminiAiModelOptions: AiModelOption[] = aiModelOptions.filter(
  (option) =>
    [
      'small',
      'large',
      'reasoning',
      'gpt-6-astra',
      'gpt-5.6-sol',
      'gpt-5.6-terra',
      'gpt-5.6-luna',
      'gpt-6.1-sol',
      'gpt-6-sol',
      'gpt-6-luna',
      'gpt-5.5-instant',
      'gpt-5-mini',
      'claude-fable-5-1',
      'claude-fable-5-1-reasoning',
      'claude-fable-5',
      'claude-fable-5-reasoning',
      'claude-opus-5-5',
      'claude-opus-5-5-reasoning',
      'claude-opus-5',
      'claude-opus-5-reasoning',
      'claude-opus-4-8',
      'claude-sonnet-5-5',
      'claude-sonnet-5',
      'claude-sonnet-4-6',
      'claude-haiku-5-5',
    ].includes(option.value),
);

// Opt-out from the Gemini upgrade — routes back to the previous model
// (gpt-4o-mini) on the server. Appended after Standard/Advanced for useGemini
// presets, except those in LEGACY_MODEL_EXCLUDED_SLUGS.
export const legacyAiModelOption: AiModelOption = {
  title: 'Standard (Legacy)',
  description: 'Previous model',
  value: 'legacy',
};

// Claude options only route correctly through the modern (shouldUseChatGPTAPI)
// path, so hide them on the few legacy presets that don't use it.
const claudeModelValues = [
  'claude-fable-5-1',
  'claude-fable-5-1-reasoning',
  'claude-fable-5',
  'claude-fable-5-reasoning',
  'claude-opus-5-5',
  'claude-opus-5-5-reasoning',
  'claude-opus-5',
  'claude-opus-5-reasoning',
  'claude-opus-4-8',
  'claude-sonnet-5-5',
  'claude-sonnet-5',
  'claude-sonnet-4-6',
  'claude-haiku-5-5',
];

/**
 * Resolve the AI model options to display for a given preset.
 *
 * Mirrors the routing constraints from the server: Claude models are only
 * shown for presets that use the modern ChatGPT API path, Gemini presets get
 * the legacy opt-out appended (unless excluded). Pass `includeLegacy: false`
 * to drop the internal "Standard (Legacy)" opt-out — used by the public form,
 * where the list is an aspirational showcase rather than a routing control.
 */
export function getAiModelOptions(
  preset: Pick<Preset, 'slug' | 'useGemini' | 'shouldUseChatGPTAPI'>,
  { includeLegacy = true }: { includeLegacy?: boolean } = {},
): AiModelOption[] {
  const base = (
    preset.useGemini ? geminiAiModelOptions : aiModelOptions
  ).filter(
    (option) =>
      preset.shouldUseChatGPTAPI || !claudeModelValues.includes(option.value),
  );

  if (!preset.useGemini) return base;

  if (
    includeLegacy &&
    !LEGACY_MODEL_EXCLUDED_SLUGS.includes(preset.slug as string)
  ) {
    return [...base, legacyAiModelOption];
  }

  return base;
}
